import React, { Component, createRef, Children } from 'react';
import Slider from 'react-slick';
import styled, { css, keyframes } from 'styled-components';
import ButtonUI from '../Button/ButtonUI';
import {
  IconArrowLeft,
  IconArrowRight,
  PauseIcon,
  PlayIcon,
} from '../../../svgs/SlideShowControllers';

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: ${({ theme }) => `${theme.unit(1)} ${theme.unit(2)}`};
  margin-top: ${({ controlsTopMargin }) => `${controlsTopMargin}px`};
  margin-bottom: ${({ theme }) => theme.unit(3)};

  @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
    margin-bottom: ${({ theme }) => theme.unit(4)};
  }
`;

const ControlsGroupWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.unit(2)};
`;

const DotContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.unit(1)};
`;

const fillProgress = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
`;

const ControlButton = styled(({ buttonTheme, ...props }) => <ButtonUI {...props} theme={buttonTheme} buttonTheme={buttonTheme} />)`
  &:hover > .icon {
    color: ${({ buttonTheme, theme }) => (
    buttonTheme === 'dark'
      ? theme.c.icon.secondary
      : theme.c.icon.primary
  )} !important;
  }
`;

const DotWrapper = styled(({ active, buttonTheme, ...props }) => <ButtonUI {...props} theme={buttonTheme} buttonTheme={buttonTheme} />)`
  padding: 0;
  width: ${({ theme }) => theme.unit(2)};
  height: ${({ theme }) => theme.unit(2)};
  position: relative;
  overflow: hidden;

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  ${({ theme, buttonTheme }) => `
    border-color: ${(
    buttonTheme === 'dark'
      ? theme.c.icon.invert
      : theme.c.icon.interactive
  )};
  `}

  ${({ active, theme, buttonTheme }) => active && `
    width: ${theme.unit(6)};
    pointer-events: none;
    border-color: ${(
    buttonTheme === 'dark'
      ? theme.c.icon.invert
      : theme.c.icon.interactive
  )};
  `}

  &:hover:not(:disabled) {
    ${({ active, theme, buttonTheme }) => !active && `
      background-color: ${buttonTheme === 'dark' ? theme.c.icon.invert : theme.c.icon.primary};
      border-color: ${buttonTheme === 'dark' ? theme.c.icon.invert : theme.c.icon.primary};
    `}
  }

  &:focus:not(:disabled) {
    ${({ active, theme, buttonTheme }) => !active && `
      background-color: transparent;
      border-color: ${buttonTheme === 'dark' ? theme.c.icon.invert : theme.c.icon.interactive};
    `}
  }

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: ${({ theme, buttonTheme }) => (
    buttonTheme === 'dark'
      ? theme.c.icon.invert
      : theme.c.icon.interactive
  )};
    width: 100%;
    transform-origin: left center;
    transform: scaleX(${({ active }) => (active ? 1 : 0)});
    ${({ active, isPlaying, autoplaySpeed }) => active && css`
      animation: ${fillProgress} ${autoplaySpeed}ms linear forwards;
      animation-play-state: ${isPlaying ? 'running' : 'paused'};
    `}
  }
`;

class SlideShow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isPlaying: true,
      activeSlide: 0,
    };
    this.sliderRef = createRef();
    this.autoplayTimeoutId = null;
    this.focusabilityTimeoutId = null;
    this.slideCycleStartedAt = null;

    this.sliderSettings = {
      infinite: true,
      speed: 600,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: false,
      autoplaySpeed: 5000,
      arrows: false,
      draggable: false,
      pauseOnHover: false,
      beforeChange: this.handleBeforeChange,
      afterChange: this.updateSlideFocusability,
    };
    this.remainingAutoplayMs = this.sliderSettings.autoplaySpeed;
  }

  componentDidMount() {
    this.startAutoplayIfPlaying();
    this.updateSlideFocusability();
  }

  componentWillUnmount() {
    this.clearAutoplayTimeout();
    clearTimeout(this.focusabilityTimeoutId);
    this.focusabilityTimeoutId = null;
  }

  updateSlideFocusability = () => {
    // Timeout ensures this runs after slick has updated the DOM
    clearTimeout(this.focusabilityTimeoutId);
    this.focusabilityTimeoutId = setTimeout(() => {
      const sliderEl = this.sliderRef.current;
      if (!sliderEl || !sliderEl.innerSlider || !sliderEl.innerSlider.list) return;

      const slides = sliderEl.innerSlider.list.querySelectorAll('.slick-slide');

      slides.forEach((slide) => {
        const isSlideActive = slide.classList.contains('slick-active');
        const focusableElements = slide.querySelectorAll('a, button, input, textarea, select');

        focusableElements.forEach((el) => {
          if (!isSlideActive) {
            el.setAttribute('tabindex', -1);
          } else {
            el.removeAttribute('tabindex');
          }
        });
      });
    }, 0);
  };

  getSlideCount = () => {
    const { children } = this.props;
    return Children.count(children);
  };

  clearAutoplayTimeout = () => {
    clearTimeout(this.autoplayTimeoutId);
    this.autoplayTimeoutId = null;
  };

  resetAutoplayCycle = () => {
    this.remainingAutoplayMs = this.sliderSettings.autoplaySpeed;
    this.slideCycleStartedAt = null;
  };

  startAutoplayIfPlaying = () => {
    this.clearAutoplayTimeout();
    const { isPlaying } = this.state;
    if (!isPlaying || this.getSlideCount() <= 1) {
      return;
    }

    const delay = Math.max(
      0,
      this.remainingAutoplayMs || this.sliderSettings.autoplaySpeed,
    );
    this.slideCycleStartedAt = Date.now();
    this.autoplayTimeoutId = setTimeout(() => {
      this.resetAutoplayCycle();
      this.slickNext();
    }, delay);
  };

  pauseAutoplayIfPlaying = () => {
    if (!this.autoplayTimeoutId || !this.slideCycleStartedAt) {
      this.clearAutoplayTimeout();
      this.slideCycleStartedAt = null;
      return;
    }

    const elapsed = Date.now() - this.slideCycleStartedAt;
    this.remainingAutoplayMs = Math.max(0, this.remainingAutoplayMs - elapsed);
    this.slideCycleStartedAt = null;
    this.clearAutoplayTimeout();
  };

  togglePlay = () => {
    this.setState((prevState) => ({ isPlaying: !prevState.isPlaying }), () => {
      const { isPlaying } = this.state;
      if (isPlaying) {
        this.startAutoplayIfPlaying();
      } else {
        this.pauseAutoplayIfPlaying();
      }
    });
  };

  handleBeforeChange = (oldIndex, newIndex) => {
    this.clearAutoplayTimeout();
    this.resetAutoplayCycle();
    this.setState({ activeSlide: newIndex }, () => {
      this.startAutoplayIfPlaying();
    });
  };

  goTo = (index) => {
    if (this.sliderRef.current) {
      this.sliderRef.current.slickGoTo(index);
    }
  };

  slickNext = () => {
    if (this.sliderRef.current) {
      this.sliderRef.current.slickNext();
    }
  };

  slickPrev = () => {
    if (this.sliderRef.current) {
      this.sliderRef.current.slickPrev();
    }
  };

  render() {
    const {
      contorlsTheme = 'dark',
      controlsTopMargin,
      children,
    } = this.props;
    const slides = Children.toArray(children);
    const { isPlaying, activeSlide } = this.state;
    const slideCount = slides.length;
    const hasMultipleSlides = slideCount > 1;

    return (
      <section>
        <div>
          <Slider ref={this.sliderRef} {...this.sliderSettings}>
            {slides}
          </Slider>
        </div>

        <ControlsWrapper
          controlsTopMargin={(
            typeof controlsTopMargin === 'number'
              ? controlsTopMargin
              : 16
          )}
        >
          {hasMultipleSlides && (
          <ControlsGroupWrapper role="group" aria-label="Slide navigation">
            <ControlButton
              variant="tertiary"
              buttonTheme={contorlsTheme}
              size="large"
              onClick={this.slickPrev}
              aria-label="Previous slide"
            >
              <IconArrowLeft className="icon" aria-hidden="true" />
            </ControlButton>

            <ControlButton
              variant="tertiary"
              buttonTheme={contorlsTheme}
              size="large"
              onClick={this.slickNext}
              aria-label="Next slide"
            >
              <IconArrowRight className="icon" aria-hidden="true" />
            </ControlButton>
          </ControlsGroupWrapper>
          )}
          {hasMultipleSlides && (
          <ControlsGroupWrapper>
            <DotContainer
              role="group"
              aria-label="Slide selection"
            >
              {slides.map((slide, i) => (
                <DotWrapper
                  key={slide.key || 'slide-dot'}
                  id={`slide-dot-${i}`}
                  aria-label={`Slide ${i + 1} of ${slideCount}`}
                  aria-current={i === activeSlide ? 'true' : undefined}
                  onClick={() => this.goTo(i)}
                  active={i === activeSlide}
                  isPlaying={isPlaying}
                  autoplaySpeed={this.sliderSettings.autoplaySpeed}
                  variant="secondary"
                  buttonTheme={contorlsTheme}
                  size="small"
                />
              ))}
            </DotContainer>
            <ControlButton
              onClick={this.togglePlay}
              variant="tertiary"
              buttonTheme={contorlsTheme}
              size="large"
              aria-pressed={isPlaying}
              aria-label={isPlaying ? 'Pause autoplay' : 'Start autoplay'}
            >
              {isPlaying
                ? <PauseIcon aria-hidden="true" />
                : <PlayIcon aria-hidden="true" />}
            </ControlButton>
          </ControlsGroupWrapper>
          )}
        </ControlsWrapper>
      </section>
    );
  }
}

export default SlideShow;
